<template>
    <transition name="skeleton-fade" @after-leave="$emit('after-leave')">
        <div
            v-if="visible"
            class="skeleton-layout"
            :class="layoutClass"
            :style="layoutStyles"
            :aria-busy="layoutAriaBusy"
            v-bind="$attrs"
        >
        <template v-if="preset === 'kpi'">
            <div class="skeleton-panel skeleton-layout__kpi">
                <Skeleton shape="square" size="40px" :animation="animation" />
                <div class="skeleton-layout__kpi-content">
                    <Skeleton shape="pill" width="72%" height="14px" :animation="animation" class="skeleton-layout__mb-sm" />
                    <Skeleton shape="pill" width="48%" height="24px" :animation="animation" />
                </div>
                <Skeleton shape="pill" width="48px" height="12px" :animation="animation" custom-class="skeleton-layout__kpi-link" />
            </div>
        </template>

        <template v-else-if="preset === 'panel-list'">
            <div class="skeleton-panel skeleton-layout__panel">
                <Skeleton shape="pill" width="42%" height="14px" :animation="animation" class="skeleton-layout__panel-header" />
                <div
                    v-for="index in rows"
                    :key="`skeleton-panel-row-${index}`"
                    class="skeleton-layout__list-row-action"
                    :class="{ 'skeleton-layout__list-row-action--last': index === rows }"
                >
                    <Skeleton shape="circle" :size="rowIconSize" :animation="animation" />
                    <Skeleton shape="pill" width="100%" height="12px" :animation="animation" />
                    <Skeleton shape="rounded" :width="actionWidth" height="28px" border-radius="6px" :animation="animation" />
                </div>
            </div>
        </template>

        <template v-else-if="preset === 'list-row-action'">
            <div class="skeleton-layout__list-row-action">
                <Skeleton shape="circle" :size="rowIconSize" :animation="animation" />
                <Skeleton shape="pill" width="100%" height="12px" :animation="animation" />
                <Skeleton shape="rounded" :width="actionWidth" height="28px" border-radius="6px" :animation="animation" />
            </div>
        </template>

        <template v-else-if="preset === 'dashboard'">
            <div class="skeleton-layout__dashboard">
                <div class="skeleton-layout__dashboard-kpis">
                    <div
                        v-for="index in kpiCount"
                        :key="`skeleton-dashboard-kpi-${index}`"
                        class="skeleton-panel skeleton-layout__kpi"
                    >
                        <Skeleton shape="square" size="40px" :animation="animation" />
                        <div class="skeleton-layout__kpi-content">
                            <Skeleton shape="pill" width="72%" height="14px" :animation="animation" class="skeleton-layout__mb-sm" />
                            <Skeleton shape="pill" width="48%" height="24px" :animation="animation" />
                        </div>
                        <Skeleton shape="pill" width="48px" height="12px" :animation="animation" custom-class="skeleton-layout__kpi-link" />
                    </div>
                </div>
                <div class="skeleton-layout__dashboard-grid">
                    <div class="skeleton-layout__dashboard-column">
                        <div class="skeleton-panel skeleton-layout__panel">
                            <Skeleton shape="pill" width="42%" height="14px" :animation="animation" class="skeleton-layout__panel-header" />
                            <div
                                v-for="rowIndex in panelRows[0]"
                                :key="`skeleton-dashboard-panel-1-${rowIndex}`"
                                class="skeleton-layout__list-row-action"
                                :class="{ 'skeleton-layout__list-row-action--last': rowIndex === panelRows[0] }"
                            >
                                <Skeleton shape="circle" :size="rowIconSize" :animation="animation" />
                                <Skeleton shape="pill" width="100%" height="12px" :animation="animation" />
                                <Skeleton shape="rounded" :width="actionWidth" height="28px" border-radius="6px" :animation="animation" />
                            </div>
                        </div>
                        <div class="skeleton-panel skeleton-layout__panel">
                            <Skeleton shape="pill" width="42%" height="14px" :animation="animation" class="skeleton-layout__panel-header" />
                            <div
                                v-for="rowIndex in panelRows[1]"
                                :key="`skeleton-dashboard-panel-2-${rowIndex}`"
                                class="skeleton-layout__list-row-action"
                                :class="{ 'skeleton-layout__list-row-action--last': rowIndex === panelRows[1] }"
                            >
                                <Skeleton shape="circle" :size="rowIconSize" :animation="animation" />
                                <Skeleton shape="pill" width="100%" height="12px" :animation="animation" />
                                <Skeleton shape="rounded" :width="actionWidth" height="28px" border-radius="6px" :animation="animation" />
                            </div>
                        </div>
                    </div>
                    <div
                        v-for="columnIndex in 2"
                        :key="`skeleton-dashboard-panel-col-${columnIndex}`"
                        class="skeleton-panel skeleton-layout__panel"
                    >
                        <Skeleton shape="pill" width="42%" height="14px" :animation="animation" class="skeleton-layout__panel-header" />
                        <div
                            v-for="rowIndex in panelRows[columnIndex + 1]"
                            :key="`skeleton-dashboard-panel-${columnIndex + 2}-${rowIndex}`"
                            class="skeleton-layout__list-row-action"
                            :class="{ 'skeleton-layout__list-row-action--last': rowIndex === panelRows[columnIndex + 1] }"
                        >
                            <Skeleton shape="circle" :size="rowIconSize" :animation="animation" />
                            <Skeleton shape="pill" width="100%" height="12px" :animation="animation" />
                            <Skeleton shape="rounded" :width="actionWidth" height="28px" border-radius="6px" :animation="animation" />
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <template v-else-if="preset === 'card'">
            <div class="skeleton-panel">
                <div class="skeleton-layout__card-header">
                    <Skeleton shape="circle" :size="avatarSize" :animation="animation" />
                    <div class="skeleton-layout__card-header-text">
                        <Skeleton shape="pill" width="10rem" height="1rem" :animation="animation" class="skeleton-layout__mb-sm" />
                        <Skeleton shape="pill" width="5rem" height="0.75rem" :animation="animation" class="skeleton-layout__mb-sm" />
                        <Skeleton shape="pill" width="100%" height="0.5rem" :animation="animation" />
                    </div>
                </div>
                <Skeleton width="100%" :height="mediaHeight" shape="rounded" :animation="animation" class="skeleton-layout__mb" />
                <div class="skeleton-layout__card-footer">
                    <Skeleton width="4rem" height="2rem" shape="rounded" :animation="animation" />
                    <Skeleton width="4rem" height="2rem" shape="rounded" :animation="animation" />
                </div>
            </div>
        </template>

        <template v-else-if="preset === 'list-item'">
            <div class="skeleton-layout__list-item">
                <Skeleton shape="circle" :size="avatarSize" :animation="animation" />
                <div class="skeleton-layout__list-item-text">
                    <Skeleton shape="pill" width="100%" height="1rem" :animation="animation" class="skeleton-layout__mb-sm" />
                    <Skeleton shape="pill" width="75%" height="0.875rem" :animation="animation" />
                </div>
            </div>
        </template>

        <template v-else-if="preset === 'list'">
            <div
                v-for="index in rows"
                :key="`skeleton-list-${index}`"
                class="skeleton-layout__list-item"
                :class="{ 'skeleton-layout__list-item--last': index === rows }"
            >
                <Skeleton shape="circle" :size="avatarSize" :animation="animation" />
                <div class="skeleton-layout__list-item-text">
                    <Skeleton shape="pill" width="100%" height="1rem" :animation="animation" class="skeleton-layout__mb-sm" />
                    <Skeleton shape="pill" width="75%" height="0.875rem" :animation="animation" />
                </div>
            </div>
        </template>

        <template v-else-if="preset === 'table-row'">
            <div class="skeleton-layout__table-row">
                <Skeleton
                    v-for="index in columns"
                    :key="`skeleton-table-col-${index}`"
                    shape="pill"
                    width="100%"
                    height="1rem"
                    :animation="animation"
                />
            </div>
        </template>

        <template v-else-if="preset === 'text-block'">
            <Skeleton
                v-for="index in rows"
                :key="`skeleton-text-${index}`"
                shape="pill"
                :width="getTextLineWidth(index)"
                height="0.875rem"
                :animation="animation"
                class="skeleton-layout__text-line"
            />
        </template>

        <template v-else-if="preset === 'avatar'">
            <div class="skeleton-layout__avatar">
                <Skeleton shape="circle" :size="avatarSize" :animation="animation" />
                <div class="skeleton-layout__avatar-text">
                    <Skeleton shape="pill" width="8rem" height="1rem" :animation="animation" class="skeleton-layout__mb-sm" />
                    <Skeleton shape="pill" width="5rem" height="0.75rem" :animation="animation" />
                </div>
            </div>
        </template>

        <template v-else>
            <slot />
        </template>
        </div>
    </transition>
</template>

<script>
import Skeleton from './Skeleton.vue'

const PRESETS = [
    'custom',
    'kpi',
    'panel-list',
    'list-row-action',
    'dashboard',
    'card',
    'list-item',
    'list',
    'table-row',
    'text-block',
    'avatar'
]

export default {
    name: 'SkeletonLayout',
    components: {
        Skeleton
    },
    inheritAttrs: false,
    props: {
        visible: {
            type: Boolean,
            default: true
        },
        fadeDuration: {
            type: Number,
            default: 300
        },
        preset: {
            type: String,
            default: 'custom',
            validator: (value) => PRESETS.includes(value)
        },
        rows: {
            type: Number,
            default: 4
        },
        columns: {
            type: Number,
            default: 4
        },
        kpiCount: {
            type: Number,
            default: 4
        },
        panelRows: {
            type: Array,
            default: () => [4, 2, 6, 6]
        },
        avatarSize: {
            type: String,
            default: '3rem'
        },
        rowIconSize: {
            type: String,
            default: '28px'
        },
        actionWidth: {
            type: String,
            default: '56px'
        },
        mediaHeight: {
            type: String,
            default: '150px'
        },
        animation: {
            type: String,
            default: 'wave'
        },
        layoutClass: {
            type: [String, Array, Object],
            default: null
        },
        ariaBusy: {
            type: [Boolean, String],
            default: true
        }
    },
    computed: {
        layoutStyles() {
            return {
                '--skeleton-fade-duration': `${this.fadeDuration}ms`
            }
        },
        layoutAriaBusy() {
            if (!this.visible) {
                return false
            }

            return this.ariaBusy
        }
    },
    methods: {
        getTextLineWidth(index) {
            if (index === this.rows) {
                return '60%'
            }

            return '100%'
        }
    }
}
</script>

<style lang="scss" scoped>
.skeleton-layout {
    width: 100%;

    &__mb {
        margin-bottom: rem(16px);
    }

    &__mb-sm {
        margin-bottom: rem(8px);
    }

    &__kpi {
        @include display-flex();
        @include align-items(center);

        gap: rem(16px);
        position: relative;
        min-height: rem(72px);
    }

    &__kpi-content {
        flex: 1;
        min-width: 0;
    }

    &__kpi-link {
        position: absolute;
        right: rem(16px);
        bottom: rem(16px);
    }

    &__panel-header {
        margin-bottom: rem(16px);
    }

    &__list-row-action {
        @include display-flex();
        @include align-items(center);

        gap: rem(12px);
        padding: rem(10px) 0;
        border-bottom: 1px solid $neutro-t70;

        > .skeleton:nth-child(2) {
            flex: 1;
            min-width: 0;
        }

        &--last {
            border-bottom: 0;
            padding-bottom: 0;
        }
    }

    &__dashboard-kpis {
        @include display-flex();

        gap: rem(12px);
        margin-bottom: rem(16px);
        flex-wrap: nowrap;
        overflow-x: auto;

        .skeleton-panel {
            flex: 1 1 0;
            min-width: rem(220px);
        }
    }

    &__dashboard-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: rem(16px);
        align-items: start;
    }

    &__dashboard-column {
        @include display-flex();
        @include flex-direction(column);

        gap: rem(16px);
    }

    &__card-header,
    &__list-item,
    &__avatar {
        @include display-flex();
        @include align-items(center);
    }

    &__card-header-text,
    &__list-item-text,
    &__avatar-text {
        flex: 1;
        min-width: 0;
        margin-left: rem(12px);
    }

    &__card-footer {
        @include display-flex();
        @include justify-content(space-between);

        margin-top: rem(16px);
    }

    &__list-item {
        margin-bottom: rem(16px);

        &--last {
            margin-bottom: 0;
        }
    }

    &__table-row {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(4rem, 1fr));
        gap: rem(12px);
        align-items: center;
    }

    &__text-line {
        display: block;
        margin-bottom: rem(8px);

        &:last-child {
            margin-bottom: 0;
        }
    }

    @include media('<=1100px') {
        &__dashboard-grid {
            grid-template-columns: 1fr;
        }

        &__dashboard-kpis {
            flex-wrap: nowrap;
            overflow-x: auto;
            scrollbar-width: none;

            &::-webkit-scrollbar {
                display: none;
            }
        }
    }
}

.skeleton-panel {
    background: $neutro-light;
    border: 1px solid $neutro-dark;
    border-radius: 4px;
    padding: rem(16px);
    width: 100%;
    box-sizing: border-box;
}

.skeleton-fade {
    &-leave-active {
        transition: opacity var(--skeleton-fade-duration, 300ms) ease;
    }

    &-leave-to {
        opacity: 0;
    }
}
</style>
